import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Twitter, Instagram, Shield, ExternalLink, Heart, Sparkles } from "lucide-react";

const Footer = () => {
  const location = useLocation();
  const isClientRoute = location.pathname.includes('/client');

  const content = {
    tagline: isClientRoute 
      ? "Your complete wellness companion that helps you track and improve your health journey."
      : "The AI-powered marketing system that helps health coaches grow their business with less effort.",
    trustBadge: isClientRoute
      ? "Trusted by 10,000+ users worldwide"
      : "Trusted by 100+ coaches worldwide",
    madeWith: isClientRoute
      ? "for health enthusiasts"
      : "for health coaches",
    cta: isClientRoute
      ? "Ready to start your wellness journey?"
      : "Ready to transform your coaching business?",
    instagram: isClientRoute
      ? "usekinos"
      : "usekinos.pro"
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-50 via-white to-[#c4ffa0]/5 border-t border-[#c4ffa0]/20">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#c4ffa0]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-green-100/30 rounded-full blur-2xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-8 lg:py-12">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
            
            {/* Logo and tagline section */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left space-y-3">
              <div className="flex items-center group">
                <div className="relative">
                  <img
                    src="logo_b_w.png"
                    alt="kinOS"
                    className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-full opacity-0 group-hover:opacity-20 blur-sm transition-all duration-500"></div>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">
                  kin<span className="text-[#2d4a1e]">OS</span>
                </span>
              </div>
              <p className="text-sm text-gray-600 max-w-md">
                {content.tagline}
              </p>
              <div className="flex items-center text-xs text-gray-500 bg-gradient-to-r from-[#c4ffa0]/10 to-green-50/50 px-3 py-1.5 rounded-full border border-[#c4ffa0]/20">
                <Sparkles className="w-3 h-3 mr-1.5 text-[#2d4a1e]" />
                <span>{content.trustBadge}</span>
              </div>
            </div>

            {/* Navigation links */}
            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
              <div className="flex flex-wrap justify-center gap-6 sm:gap-8">
                <Link 
                  to="/privacy-policy" 
                  className="group flex items-center text-sm text-gray-600 hover:text-[#2d4a1e] transition-all duration-300 relative"
                >
                  <Shield className="w-4 h-4 mr-1.5 transition-transform duration-300 group-hover:scale-110" />
                  <span>Privacy Policy</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0] group-hover:w-full transition-all duration-300"></div>
                </Link>
                
                <a 
                  href="https://x.com/usekinos" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center text-sm text-gray-600 hover:text-[#2d4a1e] transition-all duration-300 relative"
                >
                  <Twitter className="w-4 h-4 mr-1.5 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" />
                  <span>Twitter</span>
                  <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0] group-hover:w-full transition-all duration-300"></div>
                </a>
                
                <a 
                  href={`https://www.instagram.com/${content.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center text-sm text-gray-600 hover:text-[#2d4a1e] transition-all duration-300 relative"
                >
                  <Instagram className="w-4 h-4 mr-1.5 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" />
                  <span>Instagram</span>
                  <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0] group-hover:w-full transition-all duration-300"></div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-[#c4ffa0]/20 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="flex items-center text-sm text-gray-500 order-2 sm:order-1">
              <span>© 2025 Rezonata Labs Inc. All rights reserved.</span>
            </div>
            
            {/* Made with love indicator */}
            {/* <div className="flex items-center text-xs text-gray-400 order-1 sm:order-2 group">
              <span>Made with</span>
              <Heart className="w-3.5 h-3.5 mx-1.5 text-red-400 transition-all duration-300 group-hover:scale-125 group-hover:text-red-500" />
              <span>{content.madeWith}</span>
            </div> */}
          </div>
        </div>

        {/* Mobile-optimized contact section */}
        {!isClientRoute && (
          <div className="lg:hidden border-t border-[#c4ffa0]/20 pt-6 pb-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-3">
                {content.cta}
              </p>
              <a 
                href="https://calendly.com/usekinos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#c4ffa0] to-[#c4ffa0]/90 text-[#2d4a1e] text-sm font-semibold rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                <span>Get Started Free</span>
              </a>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </footer>
  );
};

export default Footer;